<template>
  <div>
    <!-- Version PC -->
    <span>
      <v-container v-if="!isMobile()">
        <!-- Prompt input at top with ChatSO logo above - only when no questions asked yet -->
        <v-container v-if="questions.length === 0" id="chat-input" class="mb-4">
          <v-row align="center" justify="center" class="mb-2">
            <img src="@/assets/chatso.jpg" width="80" alt="ChatSO" />
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-textarea clearable v-model="question" v-on:keyup.enter="sendMessage" @focus="closeDrawerOnFocus" label="Envoyer un message"
                v-bind:disabled="isDisabled" variant="solo-filled" rows="2">
                <template v-slot:append>
                  <v-btn icon @click="sendMessage" :disabled="isDisabled || !question" color="primary" size="small">
                    <v-icon>mdi-send</v-icon>
                  </v-btn>
                </template>
              </v-textarea>
            </v-col>
          </v-row>
        </v-container>
        <v-container>
          <v-card class="container-chat">
            <card-chat-r-d-g :questions="this.questions" :value="this.value" :nb="this.nb" :responses="this.responses"
              :sources="this.RagSources" class="chat-block">
            </card-chat-r-d-g>
          </v-card>
        </v-container>
        <!-- Prompt input at bottom after conversation starts - STICKY -->
        <v-container v-if="questions.length > 0" class="chat-input-sticky">
          <v-row align="center">
            <v-col cols="12" md="10" lg="10">
              <v-textarea clearable v-model="question" v-on:keyup.enter="sendMessage" @focus="closeDrawerOnFocus" 
                :label="contextLengthExceeded ? 'Limite de contexte atteinte - Démarrez une nouvelle conversation' : 'Envoyer un message'"
                v-bind:disabled="isDisabled || contextLengthExceeded" variant="solo-filled" rows="2"
                :hint="contextLengthExceeded ? 'La conversation est trop longue. Utilisez le bouton Nouvelle conversation pour continuer.' : ''"
                persistent-hint>
                <template v-slot:append>
                  <v-btn icon @click="sendMessage" :disabled="isDisabled || !question || contextLengthExceeded" color="primary" size="small">
                    <v-icon>mdi-send</v-icon>
                  </v-btn>
                </template>
              </v-textarea>
            </v-col>
            <v-col cols="12" md="2" lg="2" class="d-flex justify-center">
              <v-btn @click="newConversation" 
                :color="contextLengthExceeded ? 'error' : 'secondary'" 
                :variant="contextLengthExceeded ? 'elevated' : 'outlined'" 
                size="small" 
                class="new-conversation-btn"
                :class="{ 'pulse-animation': contextLengthExceeded }">
                <div class="d-flex flex-column align-center">
                  <v-icon size="small">mdi-refresh</v-icon>
                  <span class="text-caption">Nouvelle</span>
                  <span class="text-caption">conversation</span>
                </div>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-container>
      <!-- Add padding spacer when sticky input is active on desktop -->
      <div v-if="questions.length > 0 && !isMobile()" class="sticky-spacer"></div>
      <!-- Version mobile : Suppression des padding pour avoir un meilleur affichage -->
      <v-container v-if="isMobile()" class="p-1">
        <!-- Prompt input at top with ChatSO logo above for mobile - only when no questions asked yet -->
        <div v-if="questions.length === 0">
          <v-row align="center" justify="center" class="mt-3 mb-2">
            <img src="@/assets/chatso.jpg" width="80" alt="ChatSO" />
          </v-row>
          <v-textarea clearable v-model="question" v-on:keyup.enter="sendMessage" @focus="closeDrawerOnFocus" 
            :label="contextLengthExceeded ? 'Limite de contexte atteinte' : 'Envoyer un message'"
            v-bind:disabled="isDisabled || contextLengthExceeded" variant="solo-filled" rows="2" class="mb-4"
            :hint="contextLengthExceeded ? 'Conversation trop longue. Démarrez une nouvelle conversation.' : ''"
            persistent-hint>
            <template v-slot:append>
              <v-btn icon @click="sendMessage" :disabled="isDisabled || !question || contextLengthExceeded" color="primary" size="small">
                <v-icon>mdi-send</v-icon>
              </v-btn>
            </template>
          </v-textarea>
        </div>
        <v-card class="container-chat">
          <card-chat-r-d-g :questions="this.questions" :value="this.value" :nb="this.nb" :responses="this.responses"
            :sources="this.RagSources" class="chat-block" />
        </v-card>
        <!-- Prompt input at bottom after conversation starts for mobile - STICKY -->
        <div v-if="questions.length > 0" class="chat-input-sticky-mobile">
          <v-textarea clearable v-model="question" v-on:keyup.enter="sendMessage" @focus="closeDrawerOnFocus" 
            :label="contextLengthExceeded ? 'Limite de contexte atteinte' : 'Envoyer un message'"
            v-bind:disabled="isDisabled || contextLengthExceeded" variant="solo-filled" rows="2" class="mb-2"
            :hint="contextLengthExceeded ? 'Conversation trop longue. Démarrez une nouvelle conversation.' : ''"
            persistent-hint>
            <template v-slot:append>
              <v-btn icon @click="sendMessage" :disabled="isDisabled || !question || contextLengthExceeded" color="primary" size="small">
                <v-icon>mdi-send</v-icon>
              </v-btn>
            </template>
          </v-textarea>
          <v-btn @click="newConversation" 
            :color="contextLengthExceeded ? 'error' : 'secondary'" 
            :variant="contextLengthExceeded ? 'elevated' : 'outlined'" 
            block size="small" 
            class="new-conversation-btn-mobile"
            :class="{ 'pulse-animation': contextLengthExceeded }">
            <div class="d-flex align-center">
              <v-icon size="small" class="mr-2">mdi-refresh</v-icon>
              <span class="text-caption" style="line-height: 1.2; letter-spacing: -0.02em;">Nouvelle conversation</span>
            </div>
          </v-btn>
        </div>
      </v-container>
      <!-- Add padding spacer when sticky input is active on mobile -->
      <div v-if="questions.length > 0 && isMobile()" class="sticky-spacer-mobile"></div>
    </span>
  </div>
</template>

<script>
import CardChatRDG from "@/components/CardChatRDG.vue";
import config from "@/config/config.js";
import axios from "axios";
import { nextTick } from "vue";

export default {
  name: "ChatRDG",
  props: ['drawer'],
  emits: ['close-drawer'],
  components: {
    CardChatRDG,
  },

  data: () => ({
    // La question envoyée par l'utilisateur
    question: null,
    // L’historique de la conversation
    history: "",
    // Nombre de question/réponse
    nb: 0,
    // La variable destinée à recevoir la réponse du backend à temps réel.
    value: "",
    // url du backend
    urlServeur: config.apiUrl + "/answer-rag",
    // Variable pour activer/désactiver le champ de texte.
    isDisabled: false,
    // Tableau des question posées par l'utilisateur (chercheur)
    questions: [],
    // Tableau des réponses reçues par le serveur (backend).
    responses: [],

    RagSources: [],
    
    // Multi-conversations management
    currentConversationId: null,
    savedConversations: [],    
    // Context length management
    contextLengthExceeded: false,  }),
  
  mounted() {
    // Charger la liste des conversations et la plus récente
    this.loadSavedConversations();
    this.loadMostRecentConversation();
    
    // Listen for events from App.vue
    window.addEventListener('load-conversation', this.handleLoadConversationEvent);
    window.addEventListener('delete-conversation', this.handleDeleteConversationEvent);
    window.addEventListener('new-conversation', this.handleNewConversationEvent);
  },
  
  beforeUnmount() {
    // Clean up event listeners
    window.removeEventListener('load-conversation', this.handleLoadConversationEvent);
    window.removeEventListener('delete-conversation', this.handleDeleteConversationEvent);
    window.removeEventListener('new-conversation', this.handleNewConversationEvent);
  },
  
  methods: {
    /**
     * @function void Charger la liste de toutes les conversations sauvegardées
     */
    loadSavedConversations() {
      try {
        const conversationsData = localStorage.getItem('chatso_conversations');
        if (conversationsData) {
          const allConversations = JSON.parse(conversationsData);
          const now = new Date();
          
          // Filtrer les conversations de moins de 30 jours
          this.savedConversations = allConversations.filter(conv => {
            const convDate = new Date(conv.timestamp);
            const daysDiff = (now - convDate) / (1000 * 60 * 60 * 24);
            return daysDiff < 30;
          }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          
          // Sauvegarder la liste nettoyée
          this.saveSavedConversations();
        }
      } catch (error) {
        console.error('Error loading saved conversations:', error);
        this.savedConversations = [];
      }
    },
    
    /**
     * @function void Sauvegarder la liste des conversations
     */
    saveSavedConversations() {
      try {
        localStorage.setItem('chatso_conversations', JSON.stringify(this.savedConversations));
      } catch (error) {
        console.error('Error saving conversations list:', error);
      }
    },
    
    /**
     * @function void Charger la conversation la plus récente
     */
    loadMostRecentConversation() {
      if (this.savedConversations.length > 0) {
        const mostRecent = this.savedConversations[0];
        this.loadConversation(mostRecent.id);
      }
    },
    
    /**
     * @function void Charger une conversation spécifique
     */
    loadConversation(conversationId) {
      try {
        const conversation = this.savedConversations.find(c => c.id === conversationId);
        if (conversation) {
          this.questions = conversation.questions || [];
          this.responses = conversation.responses || [];
          this.RagSources = conversation.sources || [];
          this.history = conversation.history || "";
          this.nb = conversation.nb || 0;
          this.currentConversationId = conversationId;
          this.contextLengthExceeded = false;  // Reset when loading a saved conversation
          console.log('Loaded conversation:', conversation.title);
        }
      } catch (error) {
        console.error('Error loading conversation:', error);
      }
    },
    
    /**
     * @function void Supprimer une conversation
     */
    deleteConversation(conversationId) {
      this.savedConversations = this.savedConversations.filter(c => c.id !== conversationId);
      this.saveSavedConversations();
      
      // Si c'est la conversation active, réinitialiser
      if (conversationId === this.currentConversationId) {
        if (this.savedConversations.length > 0) {
          this.loadConversation(this.savedConversations[0].id);
        } else {
          this.newConversation();
        }
      }
    },
    
    /**
     * @function void Sauvegarder la conversation actuelle
     */
    saveCurrentConversation() {
      try {
        // Créer un ID si pas existant
        if (!this.currentConversationId) {
          this.currentConversationId = 'conv_' + Date.now();
        }
        
        // Créer le titre basé sur la première question
        const title = this.questions.length > 0 
          ? this.questions[0].substring(0, 50) + (this.questions[0].length > 50 ? '...' : '')
          : 'Nouvelle conversation';
        
        const conversationData = {
          id: this.currentConversationId,
          title: title,
          questions: this.questions,
          responses: this.responses,
          sources: this.RagSources,
          history: this.history,
          timestamp: new Date().toISOString(),
          nb: this.nb
        };
        
        // Mettre à jour ou ajouter la conversation
        const existingIndex = this.savedConversations.findIndex(c => c.id === this.currentConversationId);
        if (existingIndex >= 0) {
          this.savedConversations[existingIndex] = conversationData;
        } else {
          this.savedConversations.unshift(conversationData);
        }
        
        // Limiter à 20 conversations max
        if (this.savedConversations.length > 20) {
          this.savedConversations = this.savedConversations.slice(0, 20);
        }
        
        this.saveSavedConversations();
        this.emitConversationUpdate();
      } catch (error) {
        console.error('Error saving current conversation:', error);
      }
    },
    
    /**
     * @function string Formater la date
     */
    formatDate(timestamp) {
      const date = new Date(timestamp);
      const now = new Date();
      const daysDiff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === 0) return "Aujourd'hui";
      if (daysDiff === 1) return "Hier";
      if (daysDiff < 7) return `Il y a ${daysDiff} jours`;
      if (daysDiff < 30) return `Il y a ${Math.floor(daysDiff / 7)} semaine(s)`;
      return date.toLocaleDateString('fr-FR');
    },
    
    /**
     * @function void Handle load conversation event from App.vue
     */
    handleLoadConversationEvent(event) {
      if (event.detail) {
        this.loadConversation(event.detail);
      }
    },
    
    /**
     * @function void Handle delete conversation event from App.vue
     */
    handleDeleteConversationEvent(event) {
      if (event.detail) {
        this.deleteConversation(event.detail);
      }
    },
    
    /**
     * @function void Handle new conversation event from App.vue
     */
    handleNewConversationEvent() {
      this.newConversation();
    },
    
    /**
     * @function void Emit conversation updates to parent (App.vue)
     */
    emitConversationUpdate() {
      this.$emit('update-conversations', {
        conversations: this.savedConversations,
        currentId: this.currentConversationId
      });
    },
    
    /**
     * @function void La fonction qui envoie la question au backend et qui reçoit les réponses à temps réel
     * @returns {Promise<void>} La réponse du backend selon la question envoyée
     */
    sendMessage: async function () {
      // Store the question before clearing it
      const currentQuestion = this.question;
      
      this.questions.push(currentQuestion);
      // Désactivez la zone de texte afin d'empêcher l'utilisateur
      // d'envoyer d'autres questions avant d'avoir reçu la réponse de la première.
      this.isDisabled = true;
      const response = await axios.post(this.urlServeur, {
        question: currentQuestion,
        history: this.history,
      })
        .then((response) => {
          this.value = response.data.bot;
          this.RagSources.push(response.data);
          this.question = "";
        })
        .catch((error) => {
          console.error("Error:", error);
          
          // Check if it's a context length exceeded error (HTTP 413)
          if (error.response && error.response.status === 413) {
            this.contextLengthExceeded = true;
            this.value = "⚠️ La conversation a atteint la limite de contexte du modèle. Pour continuer, veuillez démarrer une nouvelle conversation en cliquant sur le bouton ci-dessous.";
            this.RagSources.push({ bot: this.value, sources: [] });
          } else {
            // Other errors
            this.value = "❌ Erreur lors de la communication avec le serveur. Veuillez réessayer.";
            this.RagSources.push({ bot: this.value, sources: [] });
          }
          
          this.question = "";
        });


      // scroll vers le bas de la page
      await nextTick();
      document.documentElement.scrollTop = document.documentElement.scrollHeight;


      this.history += `<user>: ${currentQuestion}\n<assistant>: ${this.value}\n\n`;
      // Ajout de la réponse dans le tableaux des réponses
      this.responses.push(this.value);
      // Réinitialisation de la valeur (réponse) en attendant la prochaine question
      this.value = "";
      // Incrimentation du nombre de réponses recu
      this.nb++;
      // Activer le textarea pour que le chercheur puisse envoyé une autre question
      this.isDisabled = false;
      
      // Sauvegarder la conversation actuelle
      this.saveCurrentConversation();
      
      // Close drawer after first answer on desktop (if it's open and not mobile)
      if (this.nb === 1 && !this.isMobile() && this.drawer) {
        this.$emit('close-drawer');
      }
      
      // Focus back on the input field after sending message
      await nextTick();
      const textareas = document.querySelectorAll('textarea[label="Envoyer un message"]');
      if (textareas.length > 0) {
        textareas[textareas.length - 1].focus();
      }
    },

    /**
     * @param data La réponse du backend
     * @returns {*|string|string} Transforme la réponse reçue en JSON en chaine de caractères
     */
    makeReadable: function (data) {
      if (data.startsWith("data: ")) {
        let jsonString = data.substring(6);
        try {
          let elements = JSON.parse(jsonString.trim());
          if (elements.bot !== undefined && elements.bot !== null && elements.bot !== '') {
            return elements.bot;
          } else {
            return " ";
          }
        } catch (error) {
          console.error(error);
          return "";
        }
      } else {
        return "";
      }
    },
    /**
     * @function void détecte le type de client, que ce soit un appareil mobile ou un ordinateur.
     * @returns {boolean} Renvoie true si c'est un appareil mobile, sinon renvoie false.
     */
    isMobile() {
      if (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        )
      ) {
        return true;
      } else {
        return false;
      }
    },
    
    /**
     * @function void Close drawer when user focuses on the prompt field
     */
    closeDrawerOnFocus() {
      if (!this.isMobile() && this.drawer) {
        this.$emit('close-drawer');
      }
    },
    
    /**
     * @function void Reset conversation to start fresh
     */
    newConversation() {
      this.questions = [];
      this.responses = [];
      this.RagSources = [];
      this.history = "";
      this.value = "";
      this.question = null;
      this.nb = 0;
      this.isDisabled = false;
      this.currentConversationId = null;
      this.contextLengthExceeded = false;  // Reset context exceeded flag
      
      // Notify App.vue of conversation change
      this.emitConversationUpdate();
      
      console.log('New conversation started');
    },
  },
};
</script>
<style>
.container-chat {
  min-height: 700px;
  box-shadow: none !important;
  overflow: auto !important;
  border: 1px solid rgba(0, 0, 0, 0.07) !important;
}

/* Conversation history styles */
.conversation-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: background-color 0.2s;
}

.conversation-item:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.conversation-item:last-child {
  border-bottom: none;
}

/* Sticky prompt input for desktop */
.chat-input-sticky {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  z-index: 4;
  padding: 16px;
  padding-left: calc(256px + 16px);
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  transition: padding-left 0.2s;
}

/* When drawer is closed on desktop */
@media (min-width: 1280px) {
  .chat-input-sticky {
    padding-left: 16px;
  }
}

/* Spacer to prevent content from being hidden behind sticky input on desktop */
.sticky-spacer {
  height: 150px;
}

/* Sticky prompt input for mobile */
.chat-input-sticky-mobile {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  z-index: 4;
  padding: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

/* Spacer to prevent content from being hidden behind sticky input on mobile */
.sticky-spacer-mobile {
  height: 140px;
}

/* New conversation button styling */
.new-conversation-btn {
  height: auto !important;
  padding: 6px 12px !important;
  min-height: 48px;
  max-width: 120px;
}

.new-conversation-btn .text-caption {
  font-size: 0.7rem !important;
  line-height: 1.1;
  letter-spacing: -0.02em;
}

.new-conversation-btn-mobile .text-caption {
  font-size: 0.75rem !important;
}

/* Pulse animation for context length exceeded */
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(244, 67, 54, 0);
  }
}

.pulse-animation {
  animation: pulse 2s infinite;
}
</style>
